import { KNOWN_EMPLOYEE_ROLES, type Employee } from '@noor/eats-api';
import type { User, UserRole } from '../model/schema';

/**
 * Wire `v1Employee` → the slice's `User` view model.
 *
 * Two things this absorbs so no widget has to:
 *
 * - **The name is a PAIR on the wire**, and either half may be empty — an
 *   identity can be attached by phone alone and named later. `fullName` is
 *   joined for display and falls back to the empty string, which the card
 *   renders as a placeholder rather than printing "undefined undefined".
 * - **`role` is an open string.** Anything outside the three roles the panel
 *   knows collapses to `unknown`, which renders as a dash. The alternative —
 *   a strict enum — would fail the whole roster parse over one novel role.
 */
export function toUser(employee: Employee): User {
  const firstName = employee.first_name.trim();
  const lastName = employee.last_name.trim();

  return {
    id: employee.employee_id,
    firstName,
    lastName,
    // The design prints «Фамилия Имя» (Мирзоев Азиз), surname first.
    fullName: [lastName, firstName].filter(Boolean).join(' '),
    role: toUserRole(employee.role),
    phone: employee.phone,
    status: employee.status,
  };
}

/** Narrow the wire's open `role` string to the roles the panel renders. */
export function toUserRole(role: string): UserRole {
  return (KNOWN_EMPLOYEE_ROLES as readonly string[]).includes(role)
    ? (role as UserRole)
    : 'unknown';
}
